import { useDeleteSlides } from "@/lib/hooks/slides/useDeleteSlides";
import { useUpdateSlide } from "@/lib/hooks/slides/useUpdateSlide";
import {
  buildMergedPassages,
  buildMergedSubQuizzes,
  canAbsorbSlide,
  isFluencyReadingSlide,
} from "@/lib/utils/subQuizMerge";
import type { SlideFields } from "@/types/Slide";
import { useState } from "react";
import toast from "react-hot-toast";

/**
 * Absorbs `sources` into `master`'s `subQuizzes` (and, when both are reading-fluency, `passages`), then
 * deletes the sources. An "other" source's cloze `elements` flatten into one SubQuiz; a reading-fluency
 * source contributes its own `subQuizzes` and `passages` directly. The master keeps its other content
 * (elements/name/limitation) — only `subQuizzes` (and `passages`, for a fluency master) changes.
 *
 * The delete is a SOFT delete server-side (`isDeleted`), but there is no user-facing way back: restore
 * only covers trashed slides, and soft-deleted ones are purged on a timer. Treat it as permanent.
 *
 * The two mutations share no transaction, so ordering is the only safety mechanism: sources are deleted
 * ONLY after the master update has committed (updateSlide swallows errors and returns undefined, e.g. a
 * linked-copy conflict). Losing the delete leaves duplicates, which is recoverable; deleting first and
 * losing the update would destroy the sources' content outright. Never reorder these.
 *
 * `sources` must already be in *selection* order (see MergeModeActionBar) — that order becomes the
 * combined subQuizzes/passages order, per buildMergedSubQuizzes/buildMergedPassages.
 */
export function useMergeSubQuizzes() {
  const { updateSlide } = useUpdateSlide();
  const { deleteSlides } = useDeleteSlides();
  const [loading, setLoading] = useState(false);

  const mergeSubQuizzes = async (master: SlideFields, sources: SlideFields[]): Promise<boolean> => {
    const masterIsFluencyReading = isFluencyReadingSlide(master);
    const eligible = sources.filter((source) => canAbsorbSlide(source, masterIsFluencyReading));
    if (eligible.length === 0) return false;

    setLoading(true);
    let masterUpdated = false;
    try {
      const masterData = (master.slideData ?? {}) as Record<string, unknown>;
      const nextSlideData = {
        ...masterData,
        subQuizzes: buildMergedSubQuizzes(master, eligible),
        ...(masterIsFluencyReading ? { passages: buildMergedPassages(master, eligible) } : {}),
      };

      const updated = await updateSlide({
        slideId: master.id,
        slideData: nextSlideData,
        elements: master.elements ?? [],
        name: master.name,
        limitation: master.limitation,
      });

      if (!updated) return false;
      masterUpdated = true;

      // `skipCheck` is the superuser bypass for "this original still has linked copies". Merge is an
      // ordinary editor action, so it must not ask for it — eligible sources have no linked copies
      // anyway (isSubQuizSource), and the server only honours the flag for superusers regardless.
      await deleteSlides(
        eligible.map((s) => s.id),
        false
      );

      toast.success(
        `Merged ${eligible.length} slide${eligible.length === 1 ? "" : "s"} into "${master.name}"`
      );
      return true;
    } catch (error) {
      console.error("Merge into subQuizzes failed:", error);
      // Only the delete can throw once the master has committed. Retrying would absorb the same
      // sources a second time and duplicate them inside the master, so don't invite one.
      toast.error(
        masterUpdated
          ? `"${master.name}" was updated, but the merged slides could not be deleted — they still exist as duplicates. Delete them manually instead of merging again.`
          : "Failed to merge slides. Please try again."
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  return { mergeSubQuizzes, loading };
}
